/**
 * SchemaPanel — Generic schema-driven design panel builder
 * Renders a Popover panel from a PanelSchema definition.
 * Handles state, persistence, OK/Cancel/Reset, presets.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Text, Popover, PopoverTrigger, PopoverContent, PopoverBody,
  useDisclosure, VStack, HStack, Select, IconButton, Tooltip, Divider,
  Slider, SliderTrack, SliderFilledTrack, SliderThumb, Button, Input,
} from '@chakra-ui/react';
import { SettingsIcon } from '@chakra-ui/icons';
import { useMultipleCharts } from './AstrologyChart/MultipleChartsContext';
import { globalEventStream } from './AstrologyChart/universal_architecture/core/EventStream';
import { PanelSchema, FieldDef, buildDefaults } from '../../config/panelSchema';

// ── Custom renderer type ──

export type CustomRenderer = (
  values: Record<string, any>,
  onChange: (patch: Record<string, any>) => void,
) => React.ReactNode;

// ── Atomic controls (exported for custom renderers) ──

export const ColorInput = ({ value, onChange, label }: {
  value: string; onChange: (c: string) => void; label?: string;
}) => (
  <HStack spacing={1}>
    {label && <Text fontSize="xs" color="gray.500" minW="20px">{label}</Text>}
    <Input
      type="color" value={value} onChange={e => onChange(e.target.value)}
      size="xs" width="28px" height="24px" p={0}
      border="1px solid" borderColor="gray.300" borderRadius="sm" cursor="pointer"
    />
  </HStack>
);

export const WidthSlider = ({ value, onChange, min = 0.3, max = 4, step = 0.1 }: {
  value: number; onChange: (v: number) => void; min?: number; max?: number; step?: number;
}) => (
  <Slider value={value} onChange={onChange} min={min} max={max} step={step} width="70px" size="sm">
    <SliderTrack><SliderFilledTrack bg="orange.300" /></SliderTrack>
    <SliderThumb boxSize={3} />
  </Slider>
);

export const OpacitySlider = ({ value, onChange }: {
  value: number; onChange: (v: number) => void;
}) => (
  <Slider value={value} onChange={onChange} min={0} max={1} step={0.05} width="50px" size="sm">
    <SliderTrack><SliderFilledTrack bg="orange.300" /></SliderTrack>
    <SliderThumb boxSize={3} />
  </Slider>
);

// ── Field renderer ──

function renderField(
  field: FieldDef,
  values: Record<string, any>,
  onChange: (patch: Record<string, any>) => void,
  customRenderers?: Record<string, CustomRenderer>,
): React.ReactNode {
  const { key, type } = field;

  switch (type) {
    case 'color':
      return <ColorInput value={values[key] ?? ''} onChange={c => onChange({ [key]: c })} label={field.label} />;

    case 'width':
      return <WidthSlider value={values[key] ?? 1} onChange={v => onChange({ [key]: v })}
        min={field.min} max={field.max} step={field.step} />;

    case 'opacity':
      return <OpacitySlider value={values[key] ?? 1} onChange={v => onChange({ [key]: v })} />;

    case 'select':
      return (
        <Select size="xs" width="70px" variant="filled"
          value={values[key] ?? ''} onChange={e => onChange({ [key]: e.target.value })}
        >
          {field.options?.map(o => <option key={o} value={o}>{o}</option>)}
        </Select>
      );

    case 'custom':
      return field.customId && customRenderers?.[field.customId]
        ? customRenderers[field.customId](values, onChange)
        : null;

    default:
      return null;
  }
}

// ── SchemaPanel component ──

interface SchemaPanelProps {
  schema: PanelSchema;
  chartId: string;
  customRenderers?: Record<string, CustomRenderer>;
  icon?: React.ReactElement;
  tooltip?: string;
  colorScheme?: string;
}

export function SchemaPanel({
  schema, chartId, customRenderers,
  icon, tooltip = 'Design', colorScheme = 'orange',
}: SchemaPanelProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { charts, updateChart } = useMultipleCharts();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const snapshotRef = useRef<Record<string, any> | null>(null);

  const defaults = useRef(buildDefaults(schema)).current;
  const saved = (charts.get(chartId)?.settings as any)?.[schema.storageKey];
  const [values, setValues] = useState<Record<string, any>>(saved ?? defaults);

  // Sync when chart changes
  useEffect(() => {
    const s = (charts.get(chartId)?.settings as any)?.[schema.storageKey];
    if (s) setValues(s);
  }, [chartId, charts, schema.storageKey]);

  // Snapshot on open for Cancel
  const handleOpen = useCallback(() => {
    snapshotRef.current = { ...values };
    onOpen();
  }, [values, onOpen]);

  // Persist + emit with debounce
  const apply = useCallback((next: Record<string, any>) => {
    setValues(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      updateChart(chartId, { settings: { [schema.storageKey]: next } } as any);
      globalEventStream.emit({
        id: `${schema.storageKey}-${chartId}-${Date.now()}`,
        type: schema.eventType,
        source: 'schema-panel',
        timestamp: Date.now(),
        chartId,
        data: { chartId, values: next },
      });
    }, 150);
  }, [chartId, updateChart, schema.storageKey, schema.eventType]);

  const onChange = useCallback(
    (patch: Record<string, any>) => apply({ ...values, ...patch }),
    [values, apply],
  );

  const handleOk = useCallback(() => onClose(), [onClose]);
  const handleCancel = useCallback(() => {
    if (snapshotRef.current) apply(snapshotRef.current);
    onClose();
  }, [onClose, apply]);
  const handleReset = useCallback(() => apply({ ...defaults }), [apply, defaults]);

  const handlePresetLoad = useCallback((name: string) => {
    const preset = schema.presets?.find(p => p.name === name);
    if (preset) apply({ ...defaults, ...preset.values });
  }, [apply, defaults, schema.presets]);

  const handleCopy = useCallback(async () => {
    try { await navigator.clipboard.writeText(JSON.stringify(values, null, 2)); }
    catch { console.log(JSON.stringify(values, null, 2)); }
  }, [values]);

  const handleLog = useCallback(() => {
    console.log(`// ${tooltip} Preset`);
    console.log(JSON.stringify(values, null, 2));
  }, [values, tooltip]);

  return (
    <Popover isOpen={isOpen} onOpen={handleOpen} onClose={onClose}
      placement="bottom-start" closeOnBlur={false}>
      <PopoverTrigger>
        <Box display="inline-block">
          <Tooltip label={tooltip} hasArrow>
            <IconButton
              aria-label={tooltip}
              icon={icon ?? <SettingsIcon />}
              size="sm" variant="ghost" height="32px" minWidth="32px" mr={1}
              _hover={{ bg: `${colorScheme}.100` }}
              color={`${colorScheme}.400`}
            />
          </Tooltip>
        </Box>
      </PopoverTrigger>

      <PopoverContent width="320px" maxH="500px" overflowY="auto">
        <PopoverBody p={3}>
          <VStack spacing={2} align="stretch">

            {schema.sections.map((section, si) => (
              <React.Fragment key={si}>
                <Text fontSize="xs" fontWeight="700" color="gray.600">{section.title}</Text>

                {section.rows.map((row, ri) => {
                  // Custom fields get full-width rendering (handle their own layout)
                  if (row.fields.some(f => f.type === 'custom')) {
                    return (
                      <React.Fragment key={ri}>
                        {row.fields.map((f, fi) => (
                          <React.Fragment key={fi}>
                            {renderField(f, values, onChange, customRenderers)}
                          </React.Fragment>
                        ))}
                      </React.Fragment>
                    );
                  }
                  // Standard row: label + controls
                  return (
                    <HStack key={ri} justify="space-between">
                      <Text fontSize="xs">{row.label}</Text>
                      <HStack spacing={1}>
                        {row.fields.map((f, fi) => (
                          <React.Fragment key={fi}>
                            {renderField(f, values, onChange, customRenderers)}
                          </React.Fragment>
                        ))}
                      </HStack>
                    </HStack>
                  );
                })}

                {si < schema.sections.length - 1 && <Divider />}
              </React.Fragment>
            ))}

            {/* Presets */}
            {schema.presets && schema.presets.length > 0 && (
              <>
                <Divider />
                <Text fontSize="xs" fontWeight="700" color="gray.600">Presets</Text>
                <HStack justify="space-between">
                  <Select size="xs" width="120px" variant="filled" placeholder="Load preset..."
                    onChange={e => { if (e.target.value) handlePresetLoad(e.target.value); e.target.value = ''; }}
                  >
                    {schema.presets.map(p => (
                      <option key={p.name} value={p.name}>{p.name}</option>
                    ))}
                  </Select>
                  <HStack spacing={1}>
                    <Tooltip label="Copy to clipboard" hasArrow>
                      <Button size="xs" variant="ghost" onClick={handleCopy}>Copy</Button>
                    </Tooltip>
                    <Tooltip label="Log to DevTools" hasArrow>
                      <Button size="xs" variant="ghost" onClick={handleLog}>Log</Button>
                    </Tooltip>
                  </HStack>
                </HStack>
                <Button size="xs" variant="outline" colorScheme="gray" onClick={handleReset} width="100%">
                  Reset to Default
                </Button>
              </>
            )}

            <Divider />
            <HStack justify="flex-end" spacing={2}>
              <Button size="xs" variant="ghost" onClick={handleCancel}>Cancel</Button>
              <Button size="xs" colorScheme={colorScheme} onClick={handleOk}>OK</Button>
            </HStack>

          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
